import { useEffect, useState } from 'react';
import { SERIES_ENABLED } from '@/lib/config';
import { fetchSeriesList } from '@/features/series/api';

/**
 * Resolves whether the Series feature is live for this client.
 *
 * Two layers (see lib/config.ts SERIES_ENABLED):
 *   1. Build-time master switch `SERIES_ENABLED`. When false → returns false
 *      immediately and performs NO network probe (feature fully dark, byte-
 *      for-byte today).
 *   2. When the build flag is true, the LIVE on/off defers to backend
 *      capability: probe `GET /series` once per session. 200 → enabled;
 *      404 (SeriesEnabledGuard dormant) or any error → disabled (fail-closed).
 *
 * This makes the eventual production flip backend-only: ship the flag-true
 * PWA once (still dark, backend 404), then flip Railway SERIES_ENABLED.
 */

let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;

async function probeSeriesCapability(): Promise<boolean> {
  if (cached !== null) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      await fetchSeriesList();
      cached = true;
    } catch {
      // 404 (dormant guard) or any transient error → fail closed.
      cached = false;
    } finally {
      inflight = null;
    }
    return cached;
  })();
  return inflight;
}

/** Test-only: reset the module-level capability cache. */
export function __resetSeriesEnabledCache(): void {
  cached = null;
  inflight = null;
}

export function useSeriesEnabled(): { enabled: boolean; loading: boolean } {
  const [enabled, setEnabled] = useState<boolean>(SERIES_ENABLED ? (cached ?? false) : false);
  const [loading, setLoading] = useState<boolean>(SERIES_ENABLED && cached === null);

  useEffect(() => {
    // When the build flag is off, or the capability is already cached, the
    // useState initializers above already hold the correct value — no effect
    // setState needed (avoids the react-hooks/set-state-in-effect cascade).
    if (!SERIES_ENABLED || cached !== null) return;
    let active = true;
    void probeSeriesCapability().then((res) => {
      if (!active) return;
      setEnabled(res);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  return { enabled, loading };
}
